import axios from "axios";
import { useState, useRef } from "react";
import { useEffect } from "react";
import packageJson from "../../../package.json";
import classNames from "classnames";
import LoginLayout from "./LoginLayout";
import validator from "validator";
import { RootState } from "redux/store";
import { useNavigate, useSearchParams } from "react-router-dom";
import useToggle from "hooks/useToggle";
import { Button, Form } from "react-bootstrap";
import Spinner from "components/Spinner";
import { updateField } from "redux/slices/Login";
import { useConstructor } from "hooks/useConstructor";
import { Link, RouteProps } from "react-router-dom";
import { updatePageRefreshCode } from "redux/slices/Login";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import {
  SnackBarMessage,
  SnackBarMessageProps,
} from "components/SnackBarMessage";
import {
  authData,
  AuthRoles,
  decodeToken,
  isLoggedIn,
  setAuthData,
} from "helpers/api/authService";
import { showToast } from "redux/slices/Toasts";
import { clearStore } from "redux/slices/Global";
import { DEFAULT_USER_PAGE_URL } from "constants/menu";

export interface HeadOfficeLoginResponse {
  headOfficeId: number;
  title: string;
}

export const useRedirect = () => {
  const dispatch = useAppDispatch();
  const [urlParams, setUrlParams] = useSearchParams();
  const navigate = useNavigate();
  return () => {
    if (isLoggedIn()) {
      dispatch(updatePageRefreshCode());
      const redirect = urlParams.get("redirect");

      let destination = "";
      const decodedToken = decodeToken();
      if (decodedToken === null) {
        destination = "/login";
      } else if (redirect !== null) {
        destination = redirect;
      } else if (decodedToken.AuthRole >= AuthRoles.Support) {
        destination = "/support/dashboard";
      } else {
        destination = DEFAULT_USER_PAGE_URL;
      }
      navigate(destination);
    }
  };
};

export function LoginPage(props: RouteProps) {
  const form = useRef<any>();
  const navigate = useNavigate();
  const redirect = useRedirect();
  const dispatch = useAppDispatch();
  const [urlParams, setUrlParams] = useSearchParams();
  const [showPassword, togglePassword] = useToggle();
  const [snackSettings, setSnackSettings] = useState<SnackBarMessageProps>(
    new SnackBarMessageProps()
  );
  const {
    email,
    rememberMe,
    password,
    passcode,
    displayMfa,
    loading,
    isMfaValid,
    isPasswordValid,
    isFormValidated,
    invalidCredentials,
  } = useAppSelector((state: RootState) => state.login);

  useEffect(() => {
    dispatch(clearStore());

    let rememberMeEmail = localStorage.getItem("email");
    if (rememberMeEmail) {
      handleChange("email", rememberMeEmail);
      handleChange("rememberMe", true);
    }
  }, []);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    } else {
      handleLogin(event);
    }
  }

  function handleLogin(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    event.stopPropagation();
    if (rememberMe) {
      localStorage.setItem("email", email);
    }

    let bodyFormData = new FormData();
    bodyFormData.append("email", email);
    bodyFormData.append("password", password);
    bodyFormData.append("passcode", passcode);
    dispatch(updateField({ key: "loading", value: true }));
    setTimeout(() => {
      axios
        .post<authData>("api/v1/UserAuth/Login", bodyFormData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then(function (response: any) {
          if (response.data.error != null) {
            dispatch(updateField({ key: "invalidCredentials", value: true }));
            if (response.data.error === "Requires Mfa Passcode") {
              dispatch(updateField({ key: "displayMfa", value: true }));
              dispatch(updateField({ key: "loading", value: false }));
            } else if (
              response.data.error === "Invalid Credentials" &&
              !displayMfa
            ) {
              dispatch(updateField({ key: "loading", value: false }));
            } else if (
              response.data.error === "Invalid Credentials" &&
              displayMfa
            ) {
              console.log("Invalid MFACode");
              dispatch(updateField({ key: "isMfaValid", value: false }));
              dispatch(updateField({ key: "loading", value: false }));
            } else {
              dispatch(showToast({ value: [response.data.error] }));
              dispatch(updateField({ key: "loading", value: false }));
              dispatch(
                updateField({ key: "invalidCredentials", value: false })
              );
            }
          }
          if (response.data.data != null) {
            setAuthData(response.data.data, rememberMe ? email || "" : "");
            dispatch(updateField({ key: "displayMfa", value: false }));
            dispatch(updateField({ key: "loading", value: false }));
            dispatch(updateField({ key: "password", value: "" }));
            dispatch(updateField({ key: "email", value: "" }));
          }
        })
        .catch(function (error) {
          if (error?.response?.data === "Requires Mfa Passcode") {
            dispatch(updateField({ key: "displayMfa", value: true }));
          } else {
            setSnackSettings({
              open: true,
              messages: [error.toString()],
              severity: "error",
              onClose: () =>
                setSnackSettings({ ...snackSettings, open: false }),
            });
            CancelLogin(false);
          }
          dispatch(updateField({ key: "loading", value: false }));
        });
    }, 1000);
  }

  function CancelLogin(clearName: boolean = true) {
    dispatch(updateField({ key: "passcode", value: "" }));
    dispatch(updateField({ key: "password", value: "" }));
    dispatch(updateField({ key: "displayMfa", value: false }));
  }

  function handleChange(option: any, optionValue: any) {
    dispatch({
      type: "loginSlice/updateField",
      payload: { key: option, value: optionValue },
    });
  }

  function checkFormValid() {
    dispatch(updateField({ key: "invalidCredentials", value: false }));
    // isPasswordValid
    const isPasswordValidated = !!password ? true : false;
    dispatch(
      updateField({ key: "isPasswordValid", value: isPasswordValidated })
    );
    // isEmailValid
    const isEmailValidated = !!validator.isEmail(email) ? true : false;
    dispatch(updateField({ key: "isEmailValid", value: isEmailValidated }));
    // isFormValidated
    dispatch(
      updateField({
        key: "isFormValidated",
        value: isPasswordValidated && isEmailValidated ? true : false,
      })
    );
  }

  useEffect(() => {
    redirect();
  }, [loading]);

  useEffect(() => {
    checkFormValid();
  }, [email, password]);

  return (
    <>
      <LoginLayout>
        <Form
          ref={form}
          noValidate
          autoComplete="off"
          // validated={isFormValidated}
          onSubmit={handleSubmit}
        >
          {!displayMfa ? (
            <div>
              <div className="text-center w-75 m-auto">
                <h4 className="text-dark-50 text-center mt-0 font-weight-bold">
                  Sign In
                </h4>
                <p className="text-muted mb-4">
                  Enter your email address and password to access the admin
                  panel.
                </p>
              </div>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="email">Email</Form.Label>
                <Form.Control
                  required
                  type="email"
                  name="email"
                  id="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => {
                    handleChange("email", e.target.value);
                  }}
                  style={{ minHeight: "38px" }}
                />
                {/* {!validator.isEmail(email) && <Form.Text className="text-danger">Please enter a valid email.</Form.Text>} */}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="password">Password</Form.Label>
                <Link
                  to="/forgot-password"
                  className="text-muted float-end"
                  tabIndex={-1}
                >
                  <small>Forgot your password?</small>
                </Link>
                <div className="mb-0 input-group">
                  <Form.Control
                    required
                    type={showPassword ? "text" : "password"}
                    name="password"
                    id="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => {
                      handleChange("password", e.target.value);
                    }}
                    style={{ minHeight: "38px" }}
                  />
                  <div
                    className={classNames(
                      "input-group-text",
                      "input-group-password",
                      { "show-password": showPassword }
                    )}
                    data-password={showPassword ? "true" : "false"}
                  >
                    <span
                      className="password-eye"
                      onClick={togglePassword}
                    ></span>
                  </div>
                </div>
                <Form.Control.Feedback type="invalid">
                  Please enter a password.
                </Form.Control.Feedback>
                {invalidCredentials ? (
                  <Form.Text className="text-danger">
                    Invalid Credentials{" "}
                  </Form.Text>
                ) : (
                  <></>
                )}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="rememberme"
                  label="Remember me"
                  checked={rememberMe}
                  onChange={(e) => handleChange("rememberMe", !rememberMe)}
                />
              </Form.Group>
              <div className="mb-3 mb-0 text-center">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={!isFormValidated}
                >
                  {loading ? (
                    <>
                      <Spinner
                        className="spinner-border-sm me-1"
                        tag="span"
                        color="white"
                      />
                    </>
                  ) : (
                    <></>
                  )}
                  {loading ? <>Loading...</> : <>Log In</>}
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <div className="text-center w-75 m-auto">
                <h4 className="text-dark-50 text-center mt-0 font-weight-bold">
                  Sign In
                </h4>
                <p className="text-muted mb-3">
                  Enter your authentication code from your authenticator app
                </p>
              </div>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="email">2FA Code</Form.Label>
                <Form.Control
                  required={!displayMfa}
                  type="text"
                  name="passcode"
                  id="passcode"
                  placeholder="Enter your 2FA Code"
                  value={passcode}
                  onChange={(e) => handleChange("passcode", e.target.value)}
                  style={{ minHeight: "38px" }}
                />
                {/* <Form.Control.Feedback type="invalid">Please enter a 2FA Code</Form.Control.Feedback> */}
                {!isMfaValid && (
                  <Form.Text className="text-danger">
                    Invalid 2FA Code
                  </Form.Text>
                )}
              </Form.Group>
              <div className="mb-3 mb-0 text-center">
                <Button
                  variant="primary m-2"
                  type="submit"
                  disabled={!isFormValidated}
                >
                  {loading ? (
                    <>
                      <Spinner
                        className="spinner-border-sm me-1"
                        tag="span"
                        color="white"
                      />
                    </>
                  ) : (
                    <></>
                  )}
                  {loading ? <>Loading...</> : <>Log In</>}
                </Button>
                <Button
                  variant="danger m-2"
                  color="error"
                  onClick={() => CancelLogin()}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </Form>
        <SnackBarMessage {...snackSettings} />
      </LoginLayout>
      <p className="zf-version">Version: {packageJson.version}</p>
    </>
  );
}
